/**
 * Utility to strip markdown formatting for TTS processing
 * Focuses on common, simple markdown patterns that work reliably.
 *
 * SUPPORTED:
 * - Headers (# ## ###)
 * - Bold (**text** and __text__)
 * - Italic (*text* and _text_)
 * - Basic links [text](url) -> text
 * - Lists (- * + 1.)
 * - Blockquotes (> text)
 * - Code blocks (```code```)
 * - Inline code (`code`)
 * - Basic HTML tags
 *
 * NOT SUPPORTED (removed for simplicity):
 * - Complex table parsing
 * - Image alt text extraction
 * - Escaped character handling
 * - Reference-style links
 * - Nested/complex formatting edge cases
 *
 * This implementation prioritizes reliability for common AI response formatting
 * over comprehensive markdown support.
 */

/**
 * Simple markdown stripping for TTS - handles common patterns reliably
 * @param {string} text - Text with markdown formatting
 * @returns {string} Plain text suitable for TTS
 */
export function stripMarkdownForTTS (text) {
  if (!text || typeof text !== 'string') {
    return ''
  }

  let cleaned = text

  // Remove code blocks (triple backticks) - remove entirely
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '')

  // Remove inline code (single backticks) - keep content
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1')

  // Remove headers (1-6 levels) - keep content
  cleaned = cleaned.replace(/^#{1,6}\s+(.*)$/gm, '$1')

  // Remove bold (**text** or __text__) - keep content
  cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1')
  cleaned = cleaned.replace(/__([^_]+)__/g, '$1')

  // Remove italic (*text* or _text_) - keep content
  cleaned = cleaned.replace(/\*([^*]+)\*/g, '$1')
  cleaned = cleaned.replace(/_([^_]+)_/g, '$1')

  // Remove basic links [text](url) - keep text only
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

  // Remove blockquotes (> text) - keep content
  cleaned = cleaned.replace(/^>\s*(.*)$/gm, '$1')

  // Remove list markers - keep content
  cleaned = cleaned.replace(/^[\s]*[-*+]\s+/gm, '')
  cleaned = cleaned.replace(/^[\s]*\d+\.\s+/gm, '')

  // Remove basic HTML tags - keep content
  cleaned = cleaned.replace(/<[^>]*>/g, '')

  // Remove horizontal rules completely
  cleaned = cleaned.replace(/^[-*_]{3,}$/gm, '')

  // Final cleanup: remove any remaining markdown characters that could be problematic for TTS
  cleaned = cleaned.replace(/[*_#>]/g, '')

  // Normalize whitespace for TTS (convert all newlines to spaces)
  cleaned = cleaned.replace(/\n+/g, ' ') // All newlines -> single space
  cleaned = cleaned.replace(/[ \t]+/g, ' ') // Multiple spaces/tabs -> single space
  cleaned = cleaned.replace(/^\s+|\s+$/g, '') // Trim start/end

  return cleaned.trim()
}

/**
 * Even simpler markdown stripping - minimal processing
 * Use this if you need the most basic cleanup with minimal risk
 */
export function stripMarkdownBasic (text) {
  if (!text || typeof text !== 'string') {
    return ''
  }

  return text
    // Remove headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold/italic markers
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    // Remove inline code
    .replace(/`([^`]+)`/g, '$1')
    // Remove basic links
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Clean whitespace
    .replace(/\s+/g, ' ')
    .trim()
}

export default {
  stripMarkdownForTTS,
  stripMarkdownBasic,
}
